import { useState, useEffect } from "react";
import { List, Tag, Button, Popconfirm, Space, Typography, Avatar } from "antd";
import { EditOutlined, DeleteOutlined, MoreOutlined, CheckSquareOutlined } from "@ant-design/icons";
import { upperFirst } from "lodash";
import { Link } from "react-router-dom";
import { firstLetterCaps, safePeek, transformType } from "../../helpers/helper";
import { FormNavigator } from "../../dynamicform/formNavigator";
import { useDispatch, useSelector } from "react-redux";
import { getIcon } from "./FuzzyIcon";
import EventSubForm from "./rentlog/EventSubForm";

const { Text } = Typography;

const configs = {
  event: {
    moreKeys: ["startDate", "endDate", "amount", "isPaid",'quantity'],
    link: "/rental/:id",
    linkId: "rentalId",
    icon: "rental.type",
    edit: (values,onClick)=>{
    return <EventSubForm  text={"Edit"} title="Edit Rental" values={values} onClick={onClick}/>
    },
  }
};

const valueTag = (key, value) => {
  if (value == null || value === "") return null;
  if (/amount|rent|price/i.test(key)) return <Tag color="green">₹{Number(value).toLocaleString()}</Tag>;
  if (key === "isPaid") return <Tag color={value ? "blue" : "default"}>{value ? "Paid" : "Unpaid"}</Tag>;
  return <Tag>{firstLetterCaps(key)}{' - '}{String(transformType(key, value))}</Tag>;
};

// optional small hook for width if you still need responsive tweaks later
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

export default function CustomList({ data = [], config = "suite", onEdit, onDelete}) {
  const dispatch = useDispatch();
  const formNavigator = FormNavigator(dispatch);
  const [expanded, setExpanded] = useState({});
  const width = useWindowWidth();
  const typesAvailable = useSelector(state=>state.workspace.types)
  const cfg = configs[config] || configs.suite;

  // const handleAssignToAll = (item) => onEdit?.(item) ?? formNavigator.agreement(item);
  const handleEdit = (item) => onEdit? onEdit(item) : formNavigator.agreement(item);
  const handleDelete = (item) => onDelete? onDelete(item) : console.warn("Delete", item.id);

  return (
    <List
      dataSource={data}
      renderItem={(item) => {
        const id = item.id || item[cfg.linkId];
        const iconKey = typesAvailable[safePeek(item, cfg.icon.split("."))]?.['name'];
        const title = item?.rental?.name || item.name || "Untitled";
        const typeText = iconKey || "";
        const desc = item?.rental?.description || item.description || "";
        const isExpanded = !!expanded[id];

        const moreTags = (cfg.moreKeys || [])
          .map((k) => {
            const v = item[k] ?? item.rental?.[k] ?? null;
            const tag = valueTag(k, v);
            return tag ? (
              <span key={k} style={{ display: "inline-flex", alignItems: "center" }}>
                 {tag}
              </span>
            ) : null;
          })
          .filter(Boolean);

        const amt = item.defaultAmount ?? item.rentAmount ?? item.rental?.defaultAmount;

        return (
          <List.Item
            style={{
              background: "#ffffff",
              borderRadius: 12,
              padding: "12px 16px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              marginBottom: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {/* ROW 1: ICON | [TITLE · TYPE] (wrap allowed) | [AMOUNT] [ACTIONS] (pinned right) */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
              <div style={{ flex: "none" }}>
                {getIcon(iconKey, true,{size:24}) || (
                  <Avatar style={{ background: "#fde3cf", color: "#f56a00", width: 44, height: 44 }}>
                    {title.charAt(0).toUpperCase()}
                  </Avatar>
                )}
              </div>

              {/* TITLE + TYPE: allow wrapping, no ellipsis */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", flexDirection: "row", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Link
                    to={cfg.link.replace(":id", item[cfg.linkId])}
                    style={{ fontWeight: 600, fontSize: 15, color: "rgba(0,0,0,0.85)", lineHeight: 1.2 }}
                    title={`${title}${typeText ? " · " + typeText : ""}`}
                  >
                    {title}
                  </Link>

                  {/* Dot separator + type shown always, wraps onto next line with title if needed */}
                  {typeText ? (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      · {upperFirst(typeText)}
                    </Text>
                  ) : null}
                </div>
              </div>

              {/* AMOUNT + ACTIONS: pinned on the right */}
              <div style={{ flex: "none", display: "flex", gap: 8, alignItems: "center" }}>
                {amt ? <Tag color="green">₹{Number(amt).toLocaleString()}</Tag> : null}

                <Space>
                  {/* <Button
                    size="small"
                    icon={cfg.edit === "Assign to All" ? <CheckSquareOutlined /> : <EditOutlined />}
                    type={cfg.edit === "Assign to All" ? "primary" : "default"}
                    onClick={() => handleEdit(item)}
                  > */}
                  {moreTags}
                    {cfg.edit(item , (values)=>handleEdit({agreementId:item.agreementId,agreementItemId:item.id,rentalId:item.rentalId,...values}))}
                  {/* </Button> */}

                  <Popconfirm
                    title="Delete this product?"
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(item)}
                  >
                    <Button size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>

                  <Button
                    size="small"
                    icon={<MoreOutlined />}
                    onClick={() => setExpanded((p) => ({ ...p, [id]: !p[id] }))}
                  >
                    {isExpanded ? "Less" : "More"}
                  </Button>
                </Space>
              </div>
            </div>

            {/* EXPANDED: single horizontal band — DESCRIPTION (left) + MORE KEYS (right, same line) */}
            {isExpanded && (
              <div style={{ display: "flex", width: "100%", gap: 16, alignItems: "flex-start", justifyContent: "space-between" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ whiteSpace: "normal", lineHeight: 1.6 }}>
                    {desc || "No description"}
                  </Text>
                </div>

                {/* <div
                  style={{
                    flex: "none",
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "nowrap",
                    maxWidth: isVerySmall ? 120 : 360,
                    overflowX: "auto",
                    paddingBottom: 4,
                  }}
                >
                  {moreTags.length ? moreTags : <Text type="secondary" style={{ fontSize: 12 }}>No extra fields</Text>}
                </div> */}
              </div>
            )}
          </List.Item>
        );
      }}
    />
  );
}
